import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Sword } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import AuthLayout from '../../components/AuthLayout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { characterClasses } from "../../utils/characterTranslation";

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    characterName: '',
    characterClass: 'GUERREIRA',
    characterLevel: '1',
    previousClan: '',
    reasonForLeaving: '',
    reasonToJoin: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorLevel, setErrorLevel] = useState('');

  const { register } = useAuth();
  const { notify } = useNotification();
  const navigate = useNavigate();

  const levelMinimo = 140;
  const textErrorLevel = `No momento recrutamos apenas ${levelMinimo}+.`;

  const handleChange = (e) => {
    if (e.target.name === 'characterLevel' && e.target.value >= levelMinimo) {
      setErrorLevel('');
    }

    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const blurLevel = (e) => {
    setErrorLevel('')
    if (e.target.name === 'characterLevel' && e.target.value < levelMinimo) {
      setErrorLevel(textErrorLevel);
    }
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      return false;
    }
    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return false;
    }
    if (!formData.characterName.trim()) {
      setError('O nome do personagem é obrigatório');
      return false;
    }
    if (parseInt(formData.characterLevel) < levelMinimo) {
      setError(textErrorLevel);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const response = await register({ 
        name: formData.name, 
        email: formData.email, 
        phone: formData.phone,
        password: formData.password,
        characterName: formData.characterName,
        characterClass: formData.characterClass,
        characterLevel: parseInt(formData.characterLevel),
        previousClan: formData.previousClan || null,
        reasonForLeaving: formData.reasonForLeaving || null,
        reasonToJoin: formData.reasonToJoin || null,
      });
      
      if (response.message && response.message.includes('Alistamento')) {
        notify.success(response.message, 'Recrutamento Enviado');
        navigate('/app/login');
      } else {
        navigate('/app/dashboard');
      }
    } catch (err) {
      console.error('Erro no recrutamento:', err);
      
      // Extrai a mensagem de erro corretamente
      let errorMessage = 'Erro ao enviar recrutamento. Tente novamente.';
      
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      notify.error(errorMessage, 'Erro no Recrutamento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white mb-2 flex items-center justify-center gap-2">
          <Sword className="w-7 h-7 text-blue-400" />
          Recrutamento do Clã
        </h2>
        <p className="text-gray-400 text-sm mb-2">
          Preencha seus dados para se alistar
        </p>
        <p className="text-amber-400 text-xs">
          ⚠️ Aguarde aprovação de um administrador
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto pr-2">
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl flex items-center gap-3">
            <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-lg">⚠️</span>
            </div>
            <div className="text-sm">{error}</div>
          </div>
        )}

        {/* Dados Pessoais */}
        <div className="bg-white/5 rounded-xl p-4 border border-white/10">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
            <User className="w-4 h-4 text-cyan-400" />
            Dados Pessoais
          </h3>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">
                Nome Completo
              </label>
              <Input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Seu nome completo"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="seu@email.com"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Telefone
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(11) 99999-9999"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Senha
                </label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Mínimo 6 caracteres"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Confirmar Senha
                </label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Digite novamente"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Dados do Personagem */}
        <div className="bg-gradient-to-br from-cyan-500/10 to-blue-600/10 rounded-xl p-4 border border-cyan-500/20">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
            <Sword className="w-4 h-4 text-cyan-400" />
            Personagem Principal
          </h3>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">
                Nome do Personagem *
              </label>
              <Input
                id="characterName"
                name="characterName"
                type="text"
                required
                value={formData.characterName}
                onChange={handleChange}
                placeholder="Nome do seu personagem no jogo"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Classe
                </label>
                <select
                  id="characterClass"
                  name="characterClass"
                  value={formData.characterClass}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                >
                  {characterClasses.map(({ value, label, emoji }) => (
                    <option key={value} value={value} className="bg-slate-800 text-white">
                      {emoji} {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Level
                </label>
                <Input
                  id="characterLevel"
                  name="characterLevel"
                  type="number"
                  min="1"
                  max="180"
                  required
                  value={formData.characterLevel}
                  onChange={handleChange}
                  onBlur={blurLevel}
                  placeholder="1-180"
                />
              </div>
            </div>
            {errorLevel && (
              <div className="text-sm text-red-400">{errorLevel}</div>
            )}
          </div>
        </div>

        {/* Informações sobre Clãs */}
        <div className="bg-gradient-to-br from-purple-500/10 to-pink-600/10 rounded-xl p-4 border border-purple-500/20">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
            <User className="w-4 h-4 text-purple-400" />
            Informações de Recrutamento
          </h3>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">
                Último Clã (opcional)
              </label>
              <Input
                id="previousClan"
                name="previousClan"
                type="text"
                value={formData.previousClan}
                onChange={handleChange}
                placeholder="Nome do seu último clã"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">
                Por que saiu do último clã? (opcional)
              </label>
              <textarea
                id="reasonForLeaving"
                name="reasonForLeaving"
                value={formData.reasonForLeaving}
                onChange={handleChange}
                placeholder="Conte-nos brevemente o motivo..."
                rows="3"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 resize-none"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">
                Por que quer entrar no nosso clã? *
              </label>
              <textarea
                id="reasonToJoin"
                name="reasonToJoin"
                value={formData.reasonToJoin}
                onChange={handleChange}
                required
                placeholder="O que te motiva a fazer parte do nosso clã?"
                rows="3"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 resize-none"
              />
            </div>
          </div>
        </div>

        <div className="pt-2">
          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={loading}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                Enviando...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Sword className="w-5 h-5" />
                Enviar Recrutamento
              </span>
            )}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default Register;
